import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, ArrowLeft, Link2, Mail, Pencil, Plus, Trash2, Unlink } from 'lucide-react';
import { CONTACT_ROLES, ORGANISATION_ROLES, archiveContact, archiveOrganisation, createContact, deleteContact, organisationHistory, restoreContact, restoreOrganisation, unlinkContact, updateContact, updateOrganisation, INVOICE_GROUPINGS, PAYMENT_METHODS, VAT_SCHEMES } from '@mkcore/crm/schema';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Textarea,
  Th,
  Tr, NativeSelect } from '@mkcore/ui';
import { ApiError, call, newIdempotencyKey } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { contactsQuery, organisationQuery } from '@/features/crm/queries';
import { ContactRoleBadges, RoleBadges } from '@/features/crm/labels';
import { clientCode, organisationErrors } from '@/features/crm/organisation-code';
import { ContactLinkDialog, type LinkTarget } from '@/features/crm/contact-link-dialog';
import { AttachmentsPanel } from '@/features/files/attachments-panel';
import { FolderPanel } from '@/features/files/folder-panel';
import { RelatedKnowledge } from '@/features/knowledge/panels';
import { LinksPanel } from '@/components/links-panel';
import { ChecksPanel } from '@/features/crm/checks-panel';
import { OrganisationPortal } from '@/features/portal/organisation-portal';
import { Meetings } from '@/features/crm/meetings';
import { Activities } from '@/features/crm/leads';
import { RecordTasks } from '@/features/projects/tasks';
import { History } from '@/components/history';
import { PilotActivity } from '@/features/dev/pilot';
import { MailDraftDialog } from '@/features/crm/mail-draft';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/org/$id')({ component: OrganisationDetail });

type Form = {
  name: string;
  code: string;
  roles: string[];
  emailDomains: string;
  website: string;
  vatNumber: string;
  registrationNumber: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  currency: string;
  notes: string;
  invoiceEmail: string;
  invoiceStreet: string;
  invoicePostalCode: string;
  invoiceCity: string;
  invoiceCountry: string;
  paymentTermDays: string;
  invoiceLanguage: string;
  vatScheme: string;
  invoiceGrouping: string;
  paymentMethod: string;
  directDebitMandate: string;
  directDebitIban: string;
  reminderDays: string;
  purchaseOrderRequired: boolean;
};
/** "10, 20" is a schedule, "-" is none, empty is the default of 7, 14 and 30 days (CO-0351). */
const reminderDaysOf = (s: string): number[] | null => {
  const v = s.trim();
  if (!v) return null;
  if (v === '-') return [];
  return v
    .split(/[,\s]+/)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);
};
const splitDomains = (s: string) =>
  s
    .split(/[,\s]+/)
    .map((d) => d.trim())
    .filter(Boolean);

/** One organisation: its fields, its contacts and archive or restore (CO-0161, CO-0191). */
function OrganisationDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const org = useQuery(organisationQuery(id));
  const canUpdate = useCan('crm:update');
  const [form, setForm] = useState<Form | null>(null);
  const [mailing, setMailing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => setForm(null), [id, org.data?.version]);

  const invalidate = [['org']];
  const save = useActionMutation(updateOrganisation, { invalidate, success: t('common.saved'), silentErrors: true });
  const archive = useActionMutation(archiveOrganisation, { invalidate, success: t('org.archivedDone') });
  const restore = useActionMutation(restoreOrganisation, { invalidate, success: t('org.restoredDone') });

  if (org.isError) return <ErrorState title={t('errors.generic')} description={message(org.error)} />;
  if (!org.data) return <Skeleton className="h-96" />;
  const o = org.data;
  const f: Form = form ?? {
    name: o.name,
    code: o.code ?? '',
    roles: o.roles,
    emailDomains: o.emailDomains.join(', '),
    website: o.website ?? '',
    vatNumber: o.vatNumber ?? '',
    registrationNumber: o.registrationNumber ?? '',
    street: o.street ?? '',
    postalCode: o.postalCode ?? '',
    city: o.city ?? '',
    country: o.country,
    currency: o.currency,
    notes: o.notes,
    invoiceEmail: o.invoiceEmail ?? '',
    invoiceStreet: o.invoiceStreet ?? '',
    invoicePostalCode: o.invoicePostalCode ?? '',
    invoiceCity: o.invoiceCity ?? '',
    invoiceCountry: o.invoiceCountry ?? '',
    paymentTermDays: o.paymentTermDays === null ? '' : String(o.paymentTermDays),
    invoiceLanguage: o.invoiceLanguage,
    vatScheme: o.vatScheme,
    invoiceGrouping: o.invoiceGrouping ?? '',
    paymentMethod: o.paymentMethod,
    directDebitMandate: o.directDebitMandate ?? '',
    directDebitIban: o.directDebitIban ?? '',
    reminderDays: o.reminderDays === null ? '' : o.reminderDays.length ? o.reminderDays.join(', ') : '-',
    purchaseOrderRequired: o.purchaseOrderRequired,
  };
  const set = (patch: Partial<Form>) => setForm({ ...f, ...patch });
  const text = (k: keyof Form, label: string, hint?: string) => (
    <Field label={label} hint={hint} error={errors[k]}>
      {(p) => <Input {...p} value={f[k] as string} disabled={!canUpdate || !!o.archivedAt} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />}
    </Field>
  );
  const submit = () =>
    save.mutate(
      {
        input: {
          id,
          name: f.name,
          code: f.code || null,
          roles: f.roles as never,
          emailDomains: splitDomains(f.emailDomains),
          website: f.website || null,
          vatNumber: f.vatNumber || null,
          registrationNumber: f.registrationNumber || null,
          street: f.street || null,
          postalCode: f.postalCode || null,
          city: f.city || null,
          country: f.country,
          currency: f.currency,
          notes: f.notes,
          invoiceEmail: f.invoiceEmail || null,
          invoiceStreet: f.invoiceStreet || null,
          invoicePostalCode: f.invoicePostalCode || null,
          invoiceCity: f.invoiceCity || null,
          invoiceCountry: f.invoiceCountry || null,
          paymentTermDays: f.paymentTermDays === '' ? null : Number(f.paymentTermDays),
          invoiceLanguage: f.invoiceLanguage as 'nl' | 'en',
          vatScheme: f.vatScheme as 'standard' | 'reverse_charge' | 'outside_eu' | 'exempt',
          invoiceGrouping: (f.invoiceGrouping || null) as (typeof INVOICE_GROUPINGS)[number] | null,
          paymentMethod: f.paymentMethod as (typeof PAYMENT_METHODS)[number],
          directDebitMandate: f.directDebitMandate || null,
          directDebitIban: f.directDebitIban || null,
          reminderDays: reminderDaysOf(f.reminderDays),
          purchaseOrderRequired: f.purchaseOrderRequired,
        },
        ifMatch: o.version,
      },
      { onError: (e) => setErrors(organisationErrors(e, message, t)), onSuccess: () => setErrors({}) },
    );

  return (
    <>
      <Link to="/org" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('org.title')}
      </Link>
      <PageHeader
        eyebrow={t('org.eyebrow')}
        title={o.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <RoleBadges roles={o.roles} />
            {o.archivedAt && <Badge tone="neutral">{t('org.archived')}</Badge>}
          </span>
        }
        actions={
          canUpdate &&
          (o.archivedAt ? (
            <Button variant="secondary" loading={restore.isPending} onClick={() => restore.mutate({ input: { id } })}>
              <ArchiveRestore /> {t('org.restore')}
            </Button>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setMailing(true)}>
                <Mail /> {t('mailDraft.action')}
              </Button>
              <ConfirmButton icon={<Archive />} label={t('org.archive')} title={t('org.archiveTitle')} description={t('org.archiveText')} onConfirm={() => archive.mutateAsync({ input: { id } })} />
            </div>
          ))
        }
      />
      {mailing && <MailDraftDialog organisationId={id} onClose={() => setMailing(false)} />}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card className="grid content-start gap-3 p-4">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
            {text('name', t('common.name'))}
            <Field label={t('org.fields.code')} hint={t('org.fields.codeHint')} error={errors.code}>
              {(p) => <Input {...p} value={f.code} placeholder="ACME" maxLength={10} disabled={!canUpdate || !!o.archivedAt} onChange={(e) => set({ code: clientCode(e.target.value) })} />}
            </Field>
          </div>
          <div>
            <div className="mb-1 text-sm font-bold">{t('org.fields.roles')}</div>
            <div className="flex flex-wrap gap-4">
              {ORGANISATION_ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={f.roles.includes(r)}
                    disabled={!canUpdate || !!o.archivedAt}
                    onCheckedChange={(v) => set({ roles: v === true ? [...f.roles, r] : f.roles.filter((x) => x !== r) })}
                  />
                  {t(`org.role.${r}`)}
                </label>
              ))}
            </div>
            {errors.roles && <p className="mt-1 text-xs text-danger">{errors.roles}</p>}
          </div>
          {text('emailDomains', t('org.fields.emailDomains'), t('org.fields.emailDomainsHint'))}
          {text('website', t('org.fields.website'))}
          <div className="grid gap-3 sm:grid-cols-2">
            {text('vatNumber', t('org.fields.vatNumber'))}
            {text('registrationNumber', t('org.fields.registrationNumber'))}
          </div>
          {text('street', t('org.fields.street'))}
          <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
            {text('postalCode', t('org.fields.postalCode'))}
            {text('city', t('org.fields.city'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('country', t('org.fields.country'))}
            {text('currency', t('org.fields.currency'))}
          </div>
          <Field label={t('org.fields.notes')}>
            {(p) => <Textarea {...p} rows={3} value={f.notes} disabled={!canUpdate || !!o.archivedAt} onChange={(e) => set({ notes: e.target.value })} />}
          </Field>
          <h2 className="mt-2 text-base font-bold">{t('org.invoicing')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {text('invoiceEmail', t('org.fields.invoiceEmail'), t('org.fields.invoiceEmailHint'))}
            {text('paymentTermDays', t('org.fields.paymentTermDays'), t('org.fields.paymentTermHint'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('org.fields.invoiceLanguage')}>
              {(p) => (
                <NativeSelect {...p} value={f.invoiceLanguage} disabled={!canUpdate || !!o.archivedAt} onChange={(e) => set({ invoiceLanguage: e.target.value })}>
                  <option value="nl">Nederlands</option>
                  <option value="en">English</option>
                </NativeSelect>
              )}
            </Field>
            <Field label={t('org.fields.vatScheme')} hint={t('org.fields.vatSchemeHint')}>
              {(p) => (
                <NativeSelect {...p} value={f.vatScheme} disabled={!canUpdate || !!o.archivedAt} onChange={(e) => set({ vatScheme: e.target.value })}>
                  {VAT_SCHEMES.map((v) => (
                    <option key={v} value={v}>
                      {t(`invoices.vatScheme.${v}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('org.fields.invoiceGrouping')}>
              {(p) => (
                <NativeSelect {...p} value={f.invoiceGrouping} disabled={!canUpdate || !!o.archivedAt} onChange={(e) => set({ invoiceGrouping: e.target.value })}>
                  <option value="">{t('org.fields.groupingDefault')}</option>
                  {INVOICE_GROUPINGS.map((g) => (
                    <option key={g} value={g}>
                      {t(`invoices.grouping.${g}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {text('reminderDays', t('org.fields.reminderDays'), t('org.fields.reminderDaysHint'))}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('org.fields.paymentMethod')} error={errors.paymentMethod}>
              {(p) => (
                <NativeSelect {...p} value={f.paymentMethod} disabled={!canUpdate || !!o.archivedAt} onChange={(e) => set({ paymentMethod: e.target.value })}>
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`org.paymentMethod.${m}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {f.paymentMethod === 'direct_debit' && text('directDebitMandate', t('org.fields.directDebitMandate'))}
            {f.paymentMethod === 'direct_debit' && text('directDebitIban', t('org.fields.directDebitIban'))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={f.purchaseOrderRequired} disabled={!canUpdate || !!o.archivedAt} onCheckedChange={(v) => set({ purchaseOrderRequired: v === true })} /> {t('org.fields.purchaseOrderRequired')}
          </label>
          {text('invoiceStreet', t('org.fields.invoiceStreet'), t('org.fields.invoiceAddressHint'))}
          <div className="grid gap-3 sm:grid-cols-[1fr_2fr_1fr]">
            {text('invoicePostalCode', t('org.fields.postalCode'))}
            {text('invoiceCity', t('org.fields.city'))}
            {text('invoiceCountry', t('org.fields.country'))}
          </div>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
          {canUpdate && !o.archivedAt && (
            <div className="flex justify-end">
              <Button disabled={!form} loading={save.isPending} onClick={submit}>
                {t('common.save')}
              </Button>
            </div>
          )}
        </Card>
        <div className="grid content-start gap-6">
          <Contacts organisationId={id} archived={!!o.archivedAt} />
          <OrganisationPortal organisationId={id} archived={!!o.archivedAt} />
          <Card className="grid content-start gap-3 p-4">
            <h2 className="text-lg font-bold">{t('meetings.title')}</h2>
            <Meetings organisationId={id} />
          </Card>
          <RecordTasks on={{ type: 'organisation', id }} archived={!!o.archivedAt} />
          <Card className="grid content-start gap-3 p-4">
            <h2 className="text-lg font-bold">{t('activities.title')}</h2>
            <Activities organisationId={id} />
          </Card>
          <LinksPanel links={o.links} editable={canUpdate && !o.archivedAt} onSave={(links) => save.mutateAsync({ input: { id, links }, ifMatch: o.version })} />
          <ChecksPanel organisationId={id} vatNumber={o.vatNumber} archived={!!o.archivedAt} />
          <AttachmentsPanel type="organisation" id={id} all archived={!!o.archivedAt} />
          <FolderPanel type="organisation" id={id} archived={!!o.archivedAt} />
          <PilotActivity organisationId={id} />
          <RelatedKnowledge type="organisation" id={id} q={o.name} />
          <Card className="p-4">
            <History queryKey={['org', 'organisation', id]} fetchPage={(cursor) => call(organisationHistory, { id, ...(cursor && { cursor }) })} />
          </Card>
        </div>
      </div>
    </>
  );
}

type ContactForm = { name: string; email: string; phone: string; jobTitle: string; roles: string[] };
const emptyContact: ContactForm = { name: '', email: '', phone: '', jobTitle: '', roles: [] };

function Contacts({ organisationId, archived }: { organisationId: string; archived: boolean }) {
  const { t } = useTranslation();
  const canCreate = useCan('crm:create');
  const canUpdate = useCan('crm:update');
  const canDelete = useCan('crm:delete');
  const [showArchived, setShowArchived] = useState(false);
  const contacts = useQuery(contactsQuery({ organisationId, includeArchived: showArchived || undefined }));
  const [editing, setEditing] = useState<{ id?: string; version?: number; form: ContactForm } | null>(null);
  const [linking, setLinking] = useState<LinkTarget | null>(null);
  const invalidate = [['org']];
  const unlink = useActionMutation(unlinkContact, { invalidate, success: t('org.contacts.unlinked') });
  const archiveC = useActionMutation(archiveContact, { invalidate });
  const restoreC = useActionMutation(restoreContact, { invalidate });
  const remove = useActionMutation(deleteContact, { invalidate, success: t('org.contacts.deleted') });
  return (
    <Card className="grid content-start gap-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">{t('org.contacts.title')}</h2>
        {canCreate && !archived && (
          <div className="flex flex-wrap gap-2">
            {canUpdate && (
              <Button size="sm" variant="ghost" onClick={() => setLinking({ jobTitle: '', roles: [] })}>
                <Link2 /> {t('org.contacts.link')}
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => setEditing({ form: emptyContact })}>
              <Plus /> {t('org.contacts.new')}
            </Button>
          </div>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(v === true)} /> {t('org.includeArchived')}
      </label>
      {contacts.isLoading ? (
        <Skeleton className="h-24" />
      ) : !contacts.data?.items.length ? (
        <EmptyState title={t('org.contacts.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('common.name')}</Th>
              <Th className="hidden sm:table-cell">{t('org.contacts.roles')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {contacts.data.items.map((c) => (
              <Tr key={c.id}>
                <Td>
                  <div className="font-bold">
                    {c.name}
                    {c.archivedAt && (
                      <Badge tone="neutral" className="ml-2">
                        {t('org.archived')}
                      </Badge>
                    )}
                    {c.linked && (
                      <Badge tone="outline" className="ml-2">
                        {t('org.contacts.linkedBadge')}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-fg-2">{[c.jobTitle, c.email, c.phone].filter(Boolean).join(' · ')}</div>
                  {!c.linked && c.links.length > 0 && <div className="text-xs text-fg-3">{t('org.contacts.alsoAt', { names: c.links.map((l) => l.organisationName).join(', ') })}</div>}
                </Td>
                <Td className="hidden sm:table-cell">
                  <ContactRoleBadges roles={c.roles} />
                </Td>
                <Td className="whitespace-nowrap text-right">
                  {canUpdate && c.linked && (
                    <>
                      <Button size="icon" variant="ghost" aria-label={t('org.contacts.editLink', { name: c.name })} onClick={() => setLinking({ contactId: c.id, name: c.name, jobTitle: c.jobTitle ?? '', roles: c.roles })}>
                        <Pencil />
                      </Button>
                      <Button size="icon" variant="ghost" aria-label={t('org.contacts.unlink', { name: c.name })} onClick={() => unlink.mutate({ input: { id: c.id, organisationId } })}>
                        <Unlink />
                      </Button>
                    </>
                  )}
                  {canUpdate && !c.linked && !c.archivedAt && (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={t('org.contacts.edit')}
                      onClick={() => setEditing({ id: c.id, version: c.version, form: { name: c.name, email: c.email ?? '', phone: c.phone ?? '', jobTitle: c.jobTitle ?? '', roles: c.roles } })}
                    >
                      <Pencil />
                    </Button>
                  )}
                  {canUpdate && !c.linked && (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={c.archivedAt ? t('org.contacts.restore') : t('org.contacts.archive')}
                      onClick={() => (c.archivedAt ? restoreC.mutate({ input: { id: c.id } }) : archiveC.mutate({ input: { id: c.id } }))}
                    >
                      {c.archivedAt ? <ArchiveRestore /> : <Archive />}
                    </Button>
                  )}
                  {canDelete && !c.linked && (
                    <Button size="icon" variant="ghost" aria-label={t('org.contacts.delete')} onClick={() => remove.mutate({ input: { id: c.id }, ifMatch: c.version })}>
                      <Trash2 />
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <ContactDialog
        key={editing?.id ?? (editing ? 'new' : 'closed')}
        organisationId={organisationId}
        editing={editing}
        onClose={() => setEditing(null)}
        onLinkExisting={(contactId, name, form) => {
          setEditing(null);
          setLinking({ contactId, name, jobTitle: form.jobTitle, roles: form.roles });
        }}
      />
      <ContactLinkDialog key={linking?.contactId ?? (linking ? 'search' : 'closed')} organisationId={organisationId} target={linking} onClose={() => setLinking(null)} />
    </Card>
  );
}

function ContactDialog({
  organisationId,
  editing,
  onClose,
  onLinkExisting,
}: {
  organisationId: string;
  editing: { id?: string; version?: number; form: ContactForm } | null;
  onClose: () => void;
  onLinkExisting: (contactId: string, name: string, form: ContactForm) => void;
}) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [key] = useState(newIdempotencyKey);
  const [f, setF] = useState(editing?.form ?? emptyContact);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useActionMutation(createContact, { invalidate: [['org']], silentErrors: true, onSuccess: onClose });
  const update = useActionMutation(updateContact, { invalidate: [['org']], silentErrors: true, onSuccess: onClose });
  const [existing, setExisting] = useState<{ id: string; name: string } | null>(null);
  const onError = (e: unknown) => {
    // The address belongs to someone already: offer to link that person to this organisation (CO-1363).
    const known = e instanceof ApiError && e.status === 409 ? /contact (.+) \(contactId ([0-9a-f-]{36})\)/.exec(e.problem.detail ?? '') : null;
    setExisting(known ? { name: known[1]!, id: known[2]! } : null);
    setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: message(e) } : { _: message(e) });
  };
  const body = { name: f.name, email: f.email || null, phone: f.phone || null, jobTitle: f.jobTitle || null, roles: f.roles as never };
  const submit = () =>
    editing?.id
      ? update.mutate({ input: { id: editing.id, ...body }, ifMatch: editing.version }, { onError })
      : create.mutate({ input: { organisationId, ...body }, key }, { onError });
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{editing?.id ? t('org.contacts.edit') : t('org.contacts.new')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('common.name')} error={errors.name}>
            {(p) => <Input {...p} autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
          </Field>
          <Field label={t('common.email')} error={errors.email}>
            {(p) => <Input {...p} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('org.contacts.phone')}>{(p) => <Input {...p} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />}</Field>
            <Field label={t('org.contacts.jobTitle')}>{(p) => <Input {...p} value={f.jobTitle} onChange={(e) => setF({ ...f, jobTitle: e.target.value })} />}</Field>
          </div>
          <div>
            <div className="mb-1 text-sm font-bold">{t('org.contacts.roles')}</div>
            <div className="flex flex-wrap gap-4">
              {CONTACT_ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={f.roles.includes(r)} onCheckedChange={(v) => setF({ ...f, roles: v === true ? [...f.roles, r] : f.roles.filter((x) => x !== r) })} />
                  {t(`org.contactRole.${r}`)}
                </label>
              ))}
            </div>
          </div>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
          {existing && !editing?.id && (
            <div>
              <Button variant="secondary" size="sm" onClick={() => onLinkExisting(existing.id, existing.name, f)}>
                <Link2 /> {t('org.contacts.linkExisting', { name: existing.name })}
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={create.isPending || update.isPending} onClick={submit}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
